import { useState } from "react";
import { Bell, BellOff, CheckCircle, XCircle } from "lucide-react";
import {
    loadNotifPrefs,
    pushDesktopNotification,
    saveNotifPrefs,
    type NotifPrefs
} from "@/lib/notifications";

type PermState = "default" | "granted" | "denied" | "unsupported";

function Toggle({
    label,
    description,
    checked,
    disabled,
    onChange,
}: {
    label: string;
    description?: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (v: boolean) => void;
}) {
    return (
        <div className={`flex items-center justify-between rounded-lg border p-3 ${disabled ? "opacity-50" : ""}`}>
            <div>
                <div className="text-sm font-medium">{label}</div>
                {description && <div className="text-xs text-muted-foreground">{description}</div>}
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => !disabled && onChange(!checked)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${checked ? "bg-primary" : "bg-muted-foreground/30"
                    }`}
            >
                <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"
                        }`}
                />
            </button>
        </div>
    );
}

async function requestPermission(): Promise<PermState> {
    if (!("Notification" in window)) return "unsupported";
    if (Notification.permission === "granted") return "granted";
    if (Notification.permission === "denied") return "denied";
    const result = await Notification.requestPermission();
    return result as PermState;
}

// Deliberately routed through pushDesktopNotification rather than constructing a Notification
// directly: this button previously had its own code path, so it kept working while real new-mail
// notifications were being silently dropped — exactly what hid the bug.
function sendTestNotification(prefs: NotifPrefs) {
    pushDesktopNotification("Athavita Mail", "This is a test notification.", { sound: prefs.sound });
}

export function NotificationsPage() {
    const [perm, setPerm] = useState<PermState>(() => {
        if (!("Notification" in window)) return "unsupported";
        return Notification.permission as PermState;
    });
    const [prefs, setPrefs] = useState<NotifPrefs>(loadNotifPrefs);
    const [requesting, setRequesting] = useState(false);

    const update = <K extends keyof NotifPrefs>(key: K, value: NotifPrefs[K]) => {
        const next = { ...prefs, [key]: value };
        setPrefs(next);
        saveNotifPrefs(next);
    };

    const handleEnable = async () => {
        setRequesting(true);
        const result = await requestPermission();
        setPerm(result);
        setRequesting(false);
    };

    const permEnabled = perm === "granted";

    return (
        <div className="max-w-xl space-y-6">
            <div>
                <h2 className="text-lg font-semibold">Notifications</h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                    Control how Athavita Mail alerts you to new messages.
                </p>
            </div>

            {perm === "unsupported" ? (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
                    <BellOff className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="text-sm text-amber-800 dark:text-amber-300">
                        <strong>Browser notifications not supported.</strong> Your browser does not support the
                        Notifications API. Try Chrome, Firefox, Edge, or Safari 16.4+.
                    </div>
                </div>
            ) : perm === "denied" ? (
                <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
                    <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                    <div className="text-sm text-red-800 dark:text-red-300">
                        <strong>Notifications are blocked.</strong> You've denied notification access.
                        To re-enable, click the lock icon in the browser's address bar and allow notifications
                        for this site, then reload.
                    </div>
                </div>
            ) : perm === "granted" ? (
                <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/30">
                    <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
                    <div className="text-sm text-green-800 dark:text-green-300">
                        <strong>Notifications are enabled.</strong> Athavita Mail can show you desktop alerts.
                    </div>
                </div>
            ) : (
                <div className="flex items-start gap-3 rounded-lg border p-4">
                    <Bell className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="flex-1 text-sm">
                        <p className="font-medium">Enable browser notifications</p>
                        <p className="mt-0.5 text-muted-foreground">
                            Get alerted when new mail arrives, even when you're in another tab.
                        </p>
                        <button
                            type="button"
                            onClick={handleEnable}
                            disabled={requesting}
                            className="mt-3 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60 hover:bg-primary/90"
                        >
                            {requesting ? "Requesting…" : "Enable notifications"}
                        </button>
                    </div>
                </div>
            )}

            <section className="space-y-2">
                <h3 className="text-sm font-semibold">Alert settings</h3>
                <Toggle
                    label="New mail notifications"
                    description="Show a desktop alert when new messages arrive in Inbox"
                    checked={prefs.newMail}
                    disabled={!permEnabled}
                    onChange={(v) => update("newMail", v)}
                />
                <Toggle
                    label="Show sender in notification"
                    description="Include the sender's name in the alert"
                    checked={prefs.showSender}
                    disabled={!permEnabled || !prefs.newMail}
                    onChange={(v) => update("showSender", v)}
                />
                <Toggle
                    label="Notification sound"
                    description="Play a sound when a notification appears"
                    checked={prefs.sound}
                    disabled={!permEnabled || !prefs.newMail}
                    onChange={(v) => update("sound", v)}
                />
            </section>

            {permEnabled && (
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => sendTestNotification(prefs)}
                        className="rounded-md border px-4 py-1.5 text-sm font-medium hover:bg-muted"
                    >
                        Send test notification
                    </button>
                    <span className="text-xs text-muted-foreground">
                        Sends a sample notification to verify your setup
                    </span>
                </div>
            )}
        </div>
    );
}
